import { useEffect, useRef, useState } from "react";
import { http, fmtNum } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Trash2, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";

const REPORTS = [
    {
        key: "tertiary",
        title: "Monthly Tertiary Sales",
        hint: "Retailer sell-out invoices (STATE, DISTRIBUTOR, RETAILER, INVOICE, EAN, QTY, MRP, DISCOUNT, NET, MONTH, CAT, GENDER).",
        sheet: "FILE TO UPLOAD",
    },
    {
        key: "dsr",
        title: "Daily Sales Report (DSR)",
        hint: "Store × day performance vs target — Location, Zone, RSM/ASM, MRP sale, Net sales, Qty, Targets.",
        sheet: "Sheet A",
    },
    {
        key: "pos_offers",
        title: "POS Current Offers",
        hint: "Offer catalog across Fresh, Common, and Yellow-ticket stores. Uploading replaces current catalog.",
        sheet: "Fresh-Stores / Common Offer / Yellow ticket",
    },
    {
        key: "eoss",
        title: "EOSS / Markdown & Credit-Note Working",
        hint: "Distributor markdown reconciliation (STATE, DIS, RETAILER, INV, EAN, QTY, MRP, DIS, NET, CATEGORY).",
        sheet: "DATA",
    },
];

export default function ReportsUpload() {
    const [uploads, setUploads] = useState([]);
    const [busyKey, setBusyKey] = useState(null);

    async function load() {
        const { data } = await http.get("/reports/uploads");
        setUploads(data);
    }

    useEffect(() => {
        load();
    }, []);

    async function handleFile(reportKey, file) {
        if (!file) return;
        setBusyKey(reportKey);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("report_type", reportKey);
        try {
            const { data } = await http.post("/reports/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(
                `${data.filename}: ingested ${fmtNum(data.rows_ingested)} rows` +
                    (data.errors?.length ? ` (${data.errors.length} warnings)` : "")
            );
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message || "Upload failed");
        } finally {
            setBusyKey(null);
        }
    }

    async function del(id) {
        if (!window.confirm("Delete this upload and all its ingested rows?")) return;
        try {
            await http.delete(`/reports/uploads/${id}`);
            toast.success("Upload deleted");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Delete failed");
        }
    }

    return (
        <div className="mx-auto max-w-[1560px] space-y-5 p-6">
            <div>
                <div className="mlabel">Reports</div>
                <h1 className="text-[22px] font-semibold tracking-tight">Daily Billing & Report Upload</h1>
                <p className="mt-1 max-w-2xl text-[12px] text-muted-foreground">
                    Upload the brand-standard MBO ops reports (.xlsx / .xlsb / .csv). Each MBO's daily billing feed lands
                    here — the platform parses, deduplicates by upload, and powers the report pages with filters, sorting,
                    and analytics.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {REPORTS.map((r) => (
                    <UploadCard
                        key={r.key}
                        report={r}
                        busy={busyKey === r.key}
                        onFile={(f) => handleFile(r.key, f)}
                    />
                ))}
            </div>

            <div className="rounded-sm border border-border bg-card">
                <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <div>
                        <div className="mlabel">Upload History</div>
                        <div className="text-[13px] font-semibold">All ingestions across this tenant</div>
                    </div>
                    <div className="text-[11px] text-muted-foreground">{uploads.length} uploads</div>
                </div>
                <div className="max-h-[420px] overflow-auto">
                    <table className="w-full text-[12px]" data-testid="upload-history-table">
                        <thead className="sticky top-0 z-10 bg-muted/60 text-[10px] uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="px-3 py-2 text-left font-semibold">When</th>
                                <th className="px-3 py-2 text-left font-semibold">Report</th>
                                <th className="px-3 py-2 text-left font-semibold">File</th>
                                <th className="px-3 py-2 text-left font-semibold">By</th>
                                <th className="px-3 py-2 text-right font-semibold">Rows</th>
                                <th className="px-3 py-2 text-left font-semibold">Warnings</th>
                                <th className="px-3 py-2 text-right font-semibold">&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            {uploads.map((u) => (
                                <tr key={u.id} data-testid={`upload-${u.id}`} className="border-t border-border hover:bg-muted/40">
                                    <td className="mono-num px-3 py-1.5 text-muted-foreground">
                                        {u.uploaded_at?.slice(0, 19).replace("T", " ")}
                                    </td>
                                    <td className="px-3 py-1.5 font-medium capitalize">
                                        {u.report_type.replace("_", " ")}
                                    </td>
                                    <td className="px-3 py-1.5 text-muted-foreground">{u.filename}</td>
                                    <td className="px-3 py-1.5 text-muted-foreground">{u.uploaded_by_name}</td>
                                    <td className="mono-num px-3 py-1.5 text-right font-semibold">
                                        {fmtNum(u.rows_ingested)}
                                    </td>
                                    <td className="px-3 py-1.5">
                                        {u.errors?.length ? (
                                            <span className="inline-flex items-center gap-1 text-[11px] text-amber-600">
                                                <AlertTriangle className="h-3 w-3" /> {u.errors.length}
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600">
                                                <CheckCircle2 className="h-3 w-3" /> Clean
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-1.5 text-right">
                                        <button
                                            onClick={() => del(u.id)}
                                            data-testid={`delete-upload-${u.id}`}
                                            className="rounded-sm p-1 text-muted-foreground hover:bg-red-500/10 hover:text-red-500"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {uploads.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-3 py-6 text-center text-[12px] text-muted-foreground">
                                        No uploads yet. Drop your first report above.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function UploadCard({ report, busy, onFile }) {
    const inputRef = useRef(null);
    const [drag, setDrag] = useState(false);

    return (
        <div
            data-testid={`upload-card-${report.key}`}
            onDragOver={(e) => {
                e.preventDefault();
                setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => {
                e.preventDefault();
                setDrag(false);
                const f = e.dataTransfer.files?.[0];
                if (f) onFile(f);
            }}
            className={
                "flex flex-col gap-3 rounded-sm border-2 border-dashed p-5 transition-colors " +
                (drag ? "border-primary bg-primary/[0.04]" : "border-border bg-card")
            }
        >
            <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                    <div className="grid h-9 w-9 place-items-center rounded-sm bg-primary/10 text-primary">
                        <FileSpreadsheet className="h-4 w-4" />
                    </div>
                    <div>
                        <div className="text-sm font-semibold">{report.title}</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            Sheet: {report.sheet}
                        </div>
                    </div>
                </div>
                {busy && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
            </div>
            <p className="text-[12px] leading-relaxed text-muted-foreground">{report.hint}</p>
            <div className="mt-1 flex items-center gap-2">
                <input
                    ref={inputRef}
                    type="file"
                    accept=".xlsx,.xlsb,.csv"
                    className="hidden"
                    data-testid={`file-input-${report.key}`}
                    onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) {
                            onFile(f);
                            e.target.value = "";
                        }
                    }}
                />
                <button
                    onClick={() => inputRef.current?.click()}
                    disabled={busy}
                    data-testid={`browse-${report.key}`}
                    className="inline-flex items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 text-[12px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                >
                    <Upload className="h-3.5 w-3.5" /> Choose file
                </button>
                <span className="text-[11px] text-muted-foreground">or drop .xlsx / .xlsb / .csv here</span>
            </div>
        </div>
    );
}
